import { addDays, format } from 'date-fns'
import {
  AlertTriangle,
  CalendarPlus,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Copy,
  Loader2,
  Sparkles,
  Trash2,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { getMealPlan } from '@/api/services/nutrition'
import type {
  FoodTemplate,
  MealLog,
  MealPlanItemCreate,
  MealPlanRead,
  MealPlanSuggestion,
  MealPlanSuggestionFood,
} from '@/api/types/nutrition'
import { BottomSheet } from '@/components/ui/bottom-sheet'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { ensureUtc } from '@/lib/date-utils'
import { useMealPlanStore } from '@/store/useMealPlanStore'

// 内部的に提案由来のアイテムを識別するための拡張型
type InternalMealPlanItem = MealPlanItemCreate & { _suggestionId?: number }

const MEAL_TYPES = ['breakfast', 'lunch', 'dinner', 'snack'] as const
const MEAL_TYPE_LABELS: Record<string, string> = {
  breakfast: '🌅 朝食',
  lunch: '☀️ 昼食',
  dinner: '🌙 夕食',
  snack: '🍪 間食',
}

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  todayDate: string
  meals: MealLog[]
  templates: FoodTemplate[]
  targetCalories: number | null
  targetProteinG: number | null
  targetFatG: number | null
  targetCarbG: number | null
}

export function MealPlanSheet({
  open,
  onOpenChange,
  todayDate,
  meals,
  templates,
  targetCalories,
  targetProteinG,
  targetFatG,
  targetCarbG,
}: Props) {
  const { currentPlan, suggestions, isLoadingSuggestion, isSaving, fetchPlan, savePlan, requestSuggestion } =
    useMealPlanStore()

  const [planTab, setPlanTab] = useState<'today' | 'tomorrow'>('today')
  const [items, setItems] = useState<InternalMealPlanItem[]>([])
  const [instruction, setInstruction] = useState('')
  const [expandedHistoryIds, setExpandedHistoryIds] = useState<Set<number>>(new Set())
  const [showTemplates, setShowTemplates] = useState<string | null>(null) // meal_type or null
  const [todayPlanForCopy, setTodayPlanForCopy] = useState<MealPlanRead | null>(null)
  const [targetMealTypes, setTargetMealTypes] = useState<Set<string>>(new Set())
  const [suggestionId, setSuggestionId] = useState(0)
  const [numSuggestions, setNumSuggestions] = useState(1)
  const [activeSuggestionIdx, setActiveSuggestionIdx] = useState(0)

  const tomorrowDate = format(addDays(new Date(todayDate + 'T00:00:00'), 1), 'yyyy-MM-dd')
  const date = planTab === 'today' ? todayDate : tomorrowDate
  const isTodayPlan = planTab === 'today'
  const sheetTitle = '食事計画'

  // 計画読み込み
  useEffect(() => {
    if (open) {
      fetchPlan(date)
      if (!isTodayPlan) {
        getMealPlan(todayDate)
          .then(setTodayPlanForCopy)
          .catch(() => setTodayPlanForCopy(null))
      } else {
        setTodayPlanForCopy(null)
      }
    }
  }, [open, date, fetchPlan, isTodayPlan, todayDate])

  // タブ切り替え時にアイテムをリセット
  useEffect(() => {
    setItems([])
    setShowTemplates(null)
  }, [planTab])

  // 既存計画があればアイテムをセット
  useEffect(() => {
    if (currentPlan?.items.length) {
      setItems(
        currentPlan.items.map((i) => ({
          meal_type: i.meal_type,
          name: i.name,
          quantity: i.quantity,
          calories: i.calories,
          protein_g: i.protein_g,
          fat_g: i.fat_g,
          carb_g: i.carb_g,
          template_id: i.template_id,
        })),
      )
    } else {
      setItems([])
    }
  }, [currentPlan])

  // 明日の計画時は記録済み食事なし
  const effectiveMeals = useMemo(() => (isTodayPlan ? meals : []), [isTodayPlan, meals])

  // 記録済み食事のPFC
  const recordedTotals = useMemo(() => {
    const cal = effectiveMeals.reduce((s, m) => s + (m.total_calories ?? 0), 0)
    const p = effectiveMeals.reduce((s, m) => s + (m.total_protein_g ?? 0), 0)
    const f = effectiveMeals.reduce((s, m) => s + (m.total_fat_g ?? 0), 0)
    const c = effectiveMeals.reduce((s, m) => s + (m.total_carb_g ?? 0), 0)
    return { calories: cal, protein_g: p, fat_g: f, carb_g: c }
  }, [effectiveMeals])

  // 記録済み食事を meal_type ごとに分類
  const recordedByType = useMemo(() => {
    const map = new Map<string, MealLog[]>()
    for (const t of MEAL_TYPES) map.set(t, [])
    for (const m of effectiveMeals) {
      const arr = map.get(m.meal_type) ?? []
      arr.push(m)
      map.set(m.meal_type, arr)
    }
    return map
  }, [effectiveMeals])

  // PFC合計（記録済み + 計画）
  const totals = useMemo(() => {
    const cal = items.reduce((s, i) => s + i.calories, 0) + recordedTotals.calories
    const p = items.reduce((s, i) => s + i.protein_g, 0) + recordedTotals.protein_g
    const f = items.reduce((s, i) => s + i.fat_g, 0) + recordedTotals.fat_g
    const c = items.reduce((s, i) => s + i.carb_g, 0) + recordedTotals.carb_g
    return { calories: cal, protein_g: p, fat_g: f, carb_g: c }
  }, [items, recordedTotals])

  // 食事種別ごとのアイテム
  const itemsByType = useMemo(() => {
    const map = new Map<string, InternalMealPlanItem[]>()
    for (const t of MEAL_TYPES) map.set(t, [])
    for (const item of items) {
      const arr = map.get(item.meal_type) ?? []
      arr.push(item)
      map.set(item.meal_type, arr)
    }
    return map
  }, [items])

  // 今日のプランをコピー（明日の計画用）
  const copyTodayPlan = useCallback(() => {
    if (!todayPlanForCopy?.items.length) return
    const copied: MealPlanItemCreate[] = todayPlanForCopy.items.map((i) => ({
      meal_type: i.meal_type,
      name: i.name,
      quantity: i.quantity,
      calories: i.calories,
      protein_g: i.protein_g,
      fat_g: i.fat_g,
      carb_g: i.carb_g,
      template_id: i.template_id,
    }))
    setItems(copied)
    toast.success('今日のプランをコピーしました')
  }, [todayPlanForCopy])

  // 計画 vs 実績の乖離（今日のみ）
  const deviation = useMemo(() => {
    if (!isTodayPlan || !currentPlan || currentPlan.items.length === 0 || effectiveMeals.length === 0) return null
    const plannedCal = currentPlan.items.reduce((s, i) => s + i.calories, 0)
    const actualCal = recordedTotals.calories
    const calDiff = actualCal - plannedCal
    // 乖離が計画の20%以上ある場合のみ表示
    if (Math.abs(calDiff) < plannedCal * 0.2) return null
    return { plannedCal, actualCal, calDiff }
  }, [isTodayPlan, currentPlan, effectiveMeals.length, recordedTotals.calories])

  // テンプレート追加
  const addTemplate = useCallback((template: FoodTemplate, mealType: string) => {
    const newItem: MealPlanItemCreate = {
      meal_type: mealType,
      name: template.name,
      quantity: null,
      calories: template.total_calories,
      protein_g: template.total_protein_g,
      fat_g: template.total_fat_g,
      carb_g: template.total_carb_g,
      template_id: template.id,
    }
    setItems((prev) => [...prev, newItem])
    setShowTemplates(null)
  }, [])

  // アイテム削除
  const removeItem = useCallback((index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index))
  }, [])

  // AI提案リクエスト（記録済み食事 + 計画アイテムを送信）
  const handleSuggest = useCallback(async () => {
    // 記録済み食事をMealPlanItemCreate形式に変換
    const recordedItems: MealPlanItemCreate[] = effectiveMeals.map((m) => ({
      meal_type: m.meal_type,
      name: m.items.map((i) => i.name).join(', ') || m.memo || '記録済み食事',
      quantity: null,
      calories: m.total_calories ?? 0,
      protein_g: m.total_protein_g ?? 0,
      fat_g: m.total_fat_g ?? 0,
      carb_g: m.total_carb_g ?? 0,
      template_id: null,
    }))
    // 乖離があれば自動的にコンテキストに追加
    let finalInstruction = instruction || null
    if (deviation) {
      const deviationNote = `※計画より${deviation.calDiff > 0 ? '+' : ''}${deviation.calDiff.toFixed(0)}kcal乖離あり。残りの食事で調整してください。`
      finalInstruction = finalInstruction ? `${finalInstruction}\n${deviationNote}` : deviationNote
    }
    try {
      const cleanItems: MealPlanItemCreate[] = items.map(({ _suggestionId: _, ...rest }) => rest)
      await requestSuggestion({
        planned_date: date,
        items: [...recordedItems, ...cleanItems],
        user_instruction: finalInstruction,
        target_meal_types: targetMealTypes.size > 0 ? [...targetMealTypes] : undefined,
        num_suggestions: numSuggestions,
      })
      setSuggestionId((prev) => prev + 1)
      setActiveSuggestionIdx(0)
      setInstruction('')
    } catch {
      toast.error('AI提案の生成に失敗しました')
    }
  }, [date, items, effectiveMeals, instruction, deviation, targetMealTypes, numSuggestions, requestSuggestion])

  // 提案アイテムを計画に採用（同じ提案の再採用は置換、別の提案は追加）
  const acceptSuggestionFoods = useCallback(
    (foods: { mealType: string; food: MealPlanSuggestionFood; quantity: string }[]) => {
      const newItems: InternalMealPlanItem[] = foods.map(({ mealType, food, quantity }) => ({
        meal_type: mealType,
        name: `${food.name} ${quantity}`,
        quantity,
        calories: food.calories,
        protein_g: food.protein_g,
        fat_g: food.fat_g,
        carb_g: food.carb_g,
        template_id: null,
        _suggestionId: suggestionId,
      }))
      // 同じ提案IDのアイテムのみ削除して置換（別の提案由来は維持）
      setItems((prev) => [...prev.filter((i) => i._suggestionId !== suggestionId), ...newItems])
    },
    [suggestionId],
  )

  // 保存
  const handleSave = useCallback(async () => {
    if (items.length === 0) {
      toast.error('食事を追加してください')
      return
    }
    try {
      // _suggestionId マーカーを除去してAPI送信
      const cleanItems: MealPlanItemCreate[] = items.map(({ _suggestionId: _, ...rest }) => rest)
      await savePlan({ planned_date: date, items: cleanItems })
      toast.success('食事計画を保存しました')
      onOpenChange(false)
    } catch {
      toast.error('保存に失敗しました')
    }
  }, [date, items, savePlan, onOpenChange])

  // 履歴アコーディオン
  const toggleHistory = useCallback((id: number) => {
    setExpandedHistoryIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  return (
    <BottomSheet title={sheetTitle} open={open} onOpenChange={onOpenChange} snapPoints={[0.92]}>
      <div className="space-y-4 overflow-y-auto px-4 pb-24">
        {/* 今日/明日 セグメントコントロール */}
        <div className="flex rounded-lg border border-border p-0.5 bg-muted/30">
          <button
            type="button"
            className={`flex-1 flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              planTab === 'today'
                ? 'bg-background shadow-sm text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => setPlanTab('today')}
          >
            <ClipboardList className="size-3.5" />
            今日
          </button>
          <button
            type="button"
            className={`flex-1 flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              planTab === 'tomorrow'
                ? 'bg-background shadow-sm text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => setPlanTab('tomorrow')}
          >
            <CalendarPlus className="size-3.5" />
            明日
          </button>
        </div>

        {/* 明日の計画: 今日のプランをコピーボタン */}
        {!isTodayPlan && todayPlanForCopy && todayPlanForCopy.items.length > 0 && items.length === 0 && (
          <Button variant="outline" className="w-full gap-2" onClick={copyTodayPlan}>
            <Copy className="size-4" />
            今日のプランをコピーして始める ({todayPlanForCopy.items.length}品)
          </Button>
        )}

        {/* 計画 vs 実績の乖離アラート（今日のみ） */}
        {deviation && (
          <section className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <AlertTriangle className="size-4 text-amber-500" />
              <span className="font-medium text-amber-700">計画との差分あり</span>
            </div>
            <p className="text-xs text-muted-foreground">
              計画: {deviation.plannedCal.toFixed(0)}kcal → 実績: {deviation.actualCal.toFixed(0)}kcal（
              <span className={deviation.calDiff > 0 ? 'text-destructive' : 'text-emerald-600'}>
                {deviation.calDiff > 0 ? '+' : ''}
                {deviation.calDiff.toFixed(0)}kcal
              </span>
              ）
            </p>
            <p className="text-xs text-muted-foreground">↓ AI提案で残りの食事を調整できます</p>
          </section>
        )}

        {/* PFC プレビュー */}
        <section className="rounded-lg bg-muted/50 p-3 space-y-2">
          <h3 className="text-xs font-medium text-muted-foreground">
            {isTodayPlan ? '記録済み + 計画 vs 目標' : '計画 vs 目標'}
          </h3>
          <GapBar label="Cal" actual={totals.calories} target={targetCalories} unit="kcal" />
          <GapBar label="P" actual={totals.protein_g} target={targetProteinG} unit="g" />
          <GapBar label="F" actual={totals.fat_g} target={targetFatG} unit="g" />
          <GapBar label="C" actual={totals.carb_g} target={targetCarbG} unit="g" />
        </section>

        {/* 食事枠リスト */}
        {MEAL_TYPES.map((mealType) => {
          const typeItems = itemsByType.get(mealType) ?? []
          const recorded = recordedByType.get(mealType) ?? []
          return (
            <section key={mealType} className="space-y-1">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium">{MEAL_TYPE_LABELS[mealType]}</h3>
                <Button variant="ghost" size="sm" className="text-xs" onClick={() => setShowTemplates(mealType)}>
                  + テンプレから追加
                </Button>
              </div>
              {/* 記録済み食事（読み取り専用） */}
              {recorded.map((meal) => (
                <div key={`rec-${meal.id}`} className="rounded bg-emerald-500/10 px-2 py-1.5 text-xs">
                  <div className="flex flex-wrap items-baseline gap-x-1 gap-y-0.5">
                    <span className="rounded bg-emerald-500/20 px-1 py-0.5 text-[10px] text-emerald-600 shrink-0">
                      記録済
                    </span>
                    <span className="font-medium break-all">
                      {meal.items.map((i) => i.name).join(', ') || meal.memo || '食事'}
                    </span>
                    <span className="text-muted-foreground shrink-0">
                      {(meal.total_calories ?? 0).toFixed(0)}kcal P{(meal.total_protein_g ?? 0).toFixed(0)} F
                      {(meal.total_fat_g ?? 0).toFixed(0)} C{(meal.total_carb_g ?? 0).toFixed(0)}
                    </span>
                  </div>
                </div>
              ))}
              {/* 計画アイテム */}
              {typeItems.map((item, idx) => {
                const globalIdx = items.indexOf(item)
                return (
                  <div
                    key={idx}
                    className="flex items-center justify-between rounded bg-background px-2 py-1.5 text-xs"
                  >
                    <div className="flex-1 min-w-0">
                      <span className="font-medium truncate">{item.name}</span>
                      <span className="text-muted-foreground ml-2">
                        {item.calories.toFixed(0)}kcal P{item.protein_g.toFixed(0)} F{item.fat_g.toFixed(0)} C
                        {item.carb_g.toFixed(0)}
                      </span>
                    </div>
                    <Button variant="ghost" size="sm" className="size-6 p-0!" onClick={() => removeItem(globalIdx)}>
                      <Trash2 className="size-3 text-destructive" />
                    </Button>
                  </div>
                )
              })}
              {recorded.length === 0 && typeItems.length === 0 && (
                <p className="text-xs text-muted-foreground pl-2">未計画</p>
              )}
            </section>
          )
        })}

        {/* テンプレート選択パネル */}
        {showTemplates && (
          <section className="rounded-lg border p-3 space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-medium">テンプレート選択 ({MEAL_TYPE_LABELS[showTemplates]})</h4>
              <Button variant="ghost" size="sm" className="text-xs" onClick={() => setShowTemplates(null)}>
                閉じる
              </Button>
            </div>
            <div className="max-h-48 overflow-y-auto space-y-1">
              {templates.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className="flex w-full items-center justify-between rounded px-2 py-1.5 text-xs hover:bg-muted transition-colors"
                  onClick={() => addTemplate(t, showTemplates)}
                >
                  <span className="font-medium truncate">{t.name}</span>
                  <span className="text-muted-foreground shrink-0 ml-2">
                    {t.total_calories.toFixed(0)}kcal P{t.total_protein_g.toFixed(0)}
                  </span>
                </button>
              ))}
              {templates.length === 0 && <p className="text-xs text-muted-foreground">テンプレートがありません</p>}
            </div>
          </section>
        )}

        {/* AI提案ボタン */}
        <section className="space-y-2">
          {/* 提案対象の食事枠選択 */}
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">提案対象（計画済み枠も追加提案可）</p>
            <div className="flex flex-wrap gap-2">
              {MEAL_TYPES.map((mt) => {
                const hasContent = (recordedByType.get(mt)?.length ?? 0) > 0 || (itemsByType.get(mt)?.length ?? 0) > 0
                const isSelected = targetMealTypes.has(mt)
                return (
                  <label
                    key={mt}
                    className={`flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs cursor-pointer transition-colors ${isSelected ? 'border-primary bg-primary/10 text-primary' : 'border-border'}`}
                  >
                    <input
                      type="checkbox"
                      className="size-3 accent-primary"
                      checked={isSelected}
                      onChange={(e) => {
                        setTargetMealTypes((prev) => {
                          const next = new Set(prev)
                          if (e.target.checked) next.add(mt)
                          else next.delete(mt)
                          return next
                        })
                      }}
                    />
                    <span>
                      {MEAL_TYPE_LABELS[mt]?.replace(/^.+\s/, '')}
                      {hasContent && <span className="ml-0.5 text-muted-foreground">(済)</span>}
                    </span>
                  </label>
                )
              })}
            </div>
          </div>
          <div className="space-y-1">
            <textarea
              className="w-full rounded-md border bg-background px-3 py-2 text-sm resize-none placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              rows={2}
              placeholder="AIへの指示 (例: 昼は外食なので牛丼に変えて、タンパク質多めで)"
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            <Button className="flex-1" onClick={handleSuggest} disabled={isLoadingSuggestion}>
              {isLoadingSuggestion ? (
                <Loader2 className="size-4 animate-spin mr-2" />
              ) : (
                <Sparkles className="size-4 mr-2" />
              )}
              {suggestions.length > 0 || (currentPlan?.suggestion_history?.length ?? 0) > 0
                ? '再提案'
                : 'AIに提案してもらう'}
            </Button>
            <select
              className="h-9 rounded-md border bg-background px-2 text-sm"
              value={numSuggestions}
              onChange={(e) => setNumSuggestions(Number(e.target.value))}
            >
              <option value={1}>1案</option>
              <option value={2}>2案</option>
              <option value={3}>3案</option>
            </select>
          </div>
        </section>

        {/* AI提案結果 */}
        {suggestions.length > 0 && (
          <>
            {suggestions.length > 1 && (
              <div className="flex gap-1">
                {suggestions.map((_, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className={`flex-1 rounded-md border px-2 py-1.5 text-xs font-medium transition-colors ${
                      activeSuggestionIdx === idx
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border hover:bg-muted/50'
                    }`}
                    onClick={() => setActiveSuggestionIdx(idx)}
                  >
                    案{idx + 1}
                  </button>
                ))}
              </div>
            )}
            <SuggestionResult
              suggestion={suggestions[activeSuggestionIdx]}
              onAcceptBatch={acceptSuggestionFoods}
              onUpdateTargetMealTypes={(types) => setTargetMealTypes(new Set(types))}
            />
          </>
        )}

        {/* 過去の提案履歴 */}
        {currentPlan?.suggestion_history && currentPlan.suggestion_history.length > 0 && (
          <section className="space-y-1">
            <h3 className="text-xs font-medium text-muted-foreground">📜 過去の提案</h3>
            {currentPlan.suggestion_history.map((h) => (
              <div key={h.id} className="rounded border">
                <button
                  type="button"
                  className="flex w-full items-center gap-1 px-2 py-1.5 text-xs hover:bg-muted/50"
                  onClick={() => toggleHistory(h.id)}
                >
                  {expandedHistoryIds.has(h.id) ? (
                    <ChevronDown className="size-3" />
                  ) : (
                    <ChevronRight className="size-3" />
                  )}
                  <span className="text-muted-foreground">
                    {new Date(ensureUtc(h.created_at)).toLocaleTimeString('ja-JP', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                  {h.user_instruction && <span className="ml-1 truncate text-foreground">"{h.user_instruction}"</span>}
                </button>
                {expandedHistoryIds.has(h.id) && <HistoryDetail json={h.suggestion_json} />}
              </div>
            ))}
          </section>
        )}

        {/* 保存ボタン */}
        <Button className="w-full" onClick={handleSave} disabled={isSaving || items.length === 0}>
          {isSaving && <Loader2 className="size-4 animate-spin mr-2" />}
          保存
        </Button>
      </div>
    </BottomSheet>
  )
}

// ==============================================================================
// Sub-components
// ==============================================================================

function GapBar({
  label,
  actual,
  target,
  unit,
}: {
  label: string
  actual: number
  target: number | null
  unit: string
}) {
  const gap = target != null ? target - actual : 0
  const pct = target && target > 0 ? Math.min((actual / target) * 100, 100) : 0

  return (
    <div className="space-y-0.5">
      <div className="flex justify-between text-xs">
        <span className="font-medium">{label}</span>
        <span className="text-muted-foreground tabular-nums">
          {actual.toFixed(0)} / {target?.toFixed(0) ?? '?'} {unit}
          {target != null && (
            <span className={gap >= 0 ? 'text-amber-500 ml-1' : 'text-emerald-500 ml-1'}>
              ({gap >= 0 ? `-${gap.toFixed(0)}` : `+${Math.abs(gap).toFixed(0)}`})
            </span>
          )}
        </span>
      </div>
      <Progress value={pct} className="h-1.5" />
    </div>
  )
}

function SuggestionResult({
  suggestion,
  onAcceptBatch,
  onUpdateTargetMealTypes,
}: {
  suggestion: MealPlanSuggestion
  onAcceptBatch: (foods: { mealType: string; food: MealPlanSuggestionFood; quantity: string }[]) => void
  onUpdateTargetMealTypes: (mealTypes: string[]) => void
}) {
  const [expandedIdx, setExpandedIdx] = useState<Set<number>>(new Set())

  // フラットな食品リスト（meal_type付き）を構築
  type FoodEntry = { mealIdx: number; foodIdx: number; mealType: string; food: MealPlanSuggestionFood }
  const allFoods: FoodEntry[] = useMemo(
    () =>
      suggestion.items.flatMap((item, mealIdx) =>
        item.foods.map((food, foodIdx) => ({ mealIdx, foodIdx, mealType: item.meal_type, food })),
      ),
    [suggestion.items],
  )

  // 食品ごとの選択状態（キー: "mealIdx-foodIdx"）
  const [selected, setSelected] = useState<Set<string>>(() => new Set(allFoods.map((f) => `${f.mealIdx}-${f.foodIdx}`)))
  // 食品ごとの量（キー: "mealIdx-foodIdx"）
  const [quantities, setQuantities] = useState<Record<string, string>>(() =>
    Object.fromEntries(allFoods.map((f) => [`${f.mealIdx}-${f.foodIdx}`, f.food.quantity])),
  )

  // 提案が変わったら全選択＆量をリセット
  useEffect(() => {
    setSelected(new Set(allFoods.map((f) => `${f.mealIdx}-${f.foodIdx}`)))
    setQuantities(Object.fromEntries(allFoods.map((f) => [`${f.mealIdx}-${f.foodIdx}`, f.food.quantity])))
    setExpandedIdx(new Set())
  }, [allFoods])

  const toggleFood = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const handleAcceptSelected = () => {
    const batch = allFoods
      .filter((f) => selected.has(`${f.mealIdx}-${f.foodIdx}`))
      .map((f) => ({
        mealType: f.mealType,
        food: f.food,
        quantity: quantities[`${f.mealIdx}-${f.foodIdx}`] ?? f.food.quantity,
      }))
    if (batch.length === 0) return
    onAcceptBatch(batch)

    // チェックが外された食品がある食事枠を再提案対象に設定
    const deselectedMealTypes = new Set(
      allFoods.filter((f) => !selected.has(`${f.mealIdx}-${f.foodIdx}`)).map((f) => f.mealType),
    )
    onUpdateTargetMealTypes([...deselectedMealTypes])
  }

  const selectedCount = selected.size
  const totalCount = allFoods.length

  return (
    <section className="space-y-3 rounded-lg border border-primary/20 bg-primary/5 p-3">
      {/* ギャップ分析 */}
      <div className="text-xs space-y-1">
        <p className="font-medium">📊 ギャップ分析</p>
        <p className="text-muted-foreground">{suggestion.gap_analysis.description}</p>
      </div>

      {/* 全体戦略 */}
      <div className="text-xs space-y-1">
        <p className="font-medium">🎯 戦略</p>
        <p className="text-muted-foreground">{suggestion.strategy}</p>
      </div>

      {/* 前回差分 */}
      {suggestion.diff_from_previous && (
        <div className="text-xs space-y-1">
          <p className="font-medium">↔️ 前回との差分</p>
          <p className="text-muted-foreground">{suggestion.diff_from_previous}</p>
        </div>
      )}

      {/* 提案アイテム（食事ごと → 食品ごと） */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium">提案メニュー</p>
          <button
            type="button"
            className="text-xs text-primary/70 hover:text-primary"
            onClick={() => {
              if (selected.size === totalCount) {
                setSelected(new Set())
              } else {
                setSelected(new Set(allFoods.map((f) => `${f.mealIdx}-${f.foodIdx}`)))
              }
            }}
          >
            {selected.size === totalCount ? 'すべて解除' : 'すべて選択'}
          </button>
        </div>

        {suggestion.items.map((item, mealIdx) => (
          <div key={mealIdx} className="space-y-1">
            {/* 食事ヘッダー */}
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium">
                <span className="mr-1">{MEAL_TYPE_LABELS[item.meal_type] ?? item.meal_type}</span>
                {item.name}
              </p>
              <button
                type="button"
                className="text-xs text-primary/70 hover:text-primary"
                onClick={() =>
                  setExpandedIdx((prev) => {
                    const next = new Set(prev)
                    if (next.has(mealIdx)) next.delete(mealIdx)
                    else next.add(mealIdx)
                    return next
                  })
                }
              >
                {expandedIdx.has(mealIdx) ? '▼ 詳細' : '▶ 詳細'}
              </button>
            </div>

            {/* 個別食品 */}
            <div className="space-y-1 pl-1">
              {item.foods.map((food, foodIdx) => {
                const key = `${mealIdx}-${foodIdx}`
                const isSelected = selected.has(key)
                return (
                  <div
                    key={key}
                    className={`flex items-center gap-2 rounded px-2 py-1 transition-colors ${
                      isSelected ? 'bg-background' : 'bg-muted/50 opacity-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleFood(key)}
                      className="size-3.5 shrink-0 accent-primary"
                    />
                    <span className="text-xs font-medium flex-1 min-w-0 truncate">{food.name}</span>
                    <input
                      type="text"
                      value={quantities[key] ?? food.quantity}
                      onChange={(e) => setQuantities((prev) => ({ ...prev, [key]: e.target.value }))}
                      className="h-5 w-20 shrink-0 rounded border border-border bg-background px-1.5 text-xs"
                    />
                    <span className="text-xs text-muted-foreground shrink-0 tabular-nums text-right">
                      P:{food.protein_g.toFixed(0)} F:{food.fat_g.toFixed(0)} C:{food.carb_g.toFixed(0)}
                    </span>
                  </div>
                )
              })}
            </div>

            {/* 詳細展開 */}
            {expandedIdx.has(mealIdx) && (
              <div className="text-xs space-y-0.5 pl-3 border-l-2 border-primary/20 text-muted-foreground">
                <p>
                  合計: Cal:{item.calories.toFixed(0)} P:{item.protein_g.toFixed(0)} F:{item.fat_g.toFixed(0)} C:
                  {item.carb_g.toFixed(0)}
                </p>
                <p>
                  <span className="font-medium text-foreground">理由:</span> {item.reason}
                </p>
                <p>
                  <span className="font-medium text-foreground">代替:</span> {item.alternative}
                </p>
                {item.timing_note && (
                  <p>
                    <span className="font-medium text-foreground">タイミング:</span> {item.timing_note}
                  </p>
                )}
                {item.practical_note && (
                  <p>
                    <span className="font-medium text-foreground">メモ:</span> {item.practical_note}
                  </p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* 一括採用ボタン */}
      <Button className="w-full" size="sm" disabled={selectedCount === 0} onClick={handleAcceptSelected}>
        選択した {selectedCount}/{totalCount} 件を採用
      </Button>

      {/* リスク・注意点 */}
      {suggestion.risks.length > 0 && (
        <div className="text-xs space-y-1">
          <p className="font-medium text-amber-600">⚠️ 注意</p>
          {suggestion.risks.map((risk, idx) => (
            <p key={idx} className="text-muted-foreground">
              • {risk}
            </p>
          ))}
        </div>
      )}
    </section>
  )
}

function HistoryDetail({ json }: { json: string }) {
  const parsed = useMemo(() => {
    try {
      return JSON.parse(json) as MealPlanSuggestion
    } catch {
      return null
    }
  }, [json])

  if (!parsed) return <p className="px-2 pb-2 text-xs text-destructive">パースエラー</p>

  return (
    <div className="px-2 pb-2 space-y-1 text-xs">
      <p className="text-muted-foreground">{parsed.strategy}</p>
      {parsed.items.map((item, idx) => (
        <div key={idx} className="flex items-center gap-2">
          <span className="text-muted-foreground">{MEAL_TYPE_LABELS[item.meal_type] ?? item.meal_type}:</span>
          <span>{item.name}</span>
          <span className="text-muted-foreground">
            ({item.calories.toFixed(0)}kcal P{item.protein_g.toFixed(0)})
          </span>
        </div>
      ))}
    </div>
  )
}
